import { Filter, Search } from 'lucide-react';
import type { ReactNode } from 'react';

import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import type { Register } from '../useRegister';

export interface RegisterToolbarProps<T> {
  /** "All routes", "All vehicles", "All drivers". */
  title: string;
  searchPlaceholder: string;
  register: Register<T>;
  /**
   * What the Filter button opens. Omit on a register with nothing to filter by,
   * and the button is not drawn — a control that opens an empty panel is worse
   * than no control.
   */
  statusOptions?: ReadonlyArray<{ value: string; label: string }>;
  /**
   * Anything this register offers beyond searching and filtering — an Export
   * button, a bulk action. Sits after Filter, so the two controls every
   * register has stay in the same place on every tab.
   */
  actions?: ReactNode;
}

/**
 * The heading, search box and filter every register in this section carries.
 *
 * The third tab to want these was the one that made them a component (CLAUDE.md
 * §3). The filter panel opens under the row rather than as a popover: it holds
 * one control today and will hold three when routes can be filtered by vehicle
 * and by capacity, and a popover that grows is a popover that ends up scrolling.
 */
export function RegisterToolbar<T>({
  title,
  searchPlaceholder,
  register,
  statusOptions,
  actions,
}: RegisterToolbarProps<T>) {
  const { query, setQuery, status, setStatus, isFilterOpen, toggleFilter } = register;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-base font-bold text-ink">{title}</h2>

        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="min-w-0 flex-1 sm:w-64 sm:flex-none">
            <Input
              label={searchPlaceholder}
              hideLabel
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
            />
          </div>

          {statusOptions && (
            <button
              type="button"
              onClick={toggleFilter}
              aria-expanded={isFilterOpen}
              className={cn(
                'inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border px-3.5 text-sm font-bold transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                isFilterOpen || status
                  ? 'border-accent-line bg-accent-soft text-accent'
                  : 'border-line bg-surface text-ink-2 hover:border-accent-line hover:text-accent',
              )}
            >
              <Filter className="h-4 w-4" aria-hidden />
              <span>Filter</span>
              {status && <span className="h-1.5 w-1.5 rounded-pill bg-current" aria-hidden />}
            </button>
          )}

          {actions}
        </div>
      </header>

      {statusOptions && isFilterOpen && (
        <div className="rounded-2xl border border-line bg-surface-2 p-3.5 sm:max-w-xs">
          <Select
            label="Status"
            options={[{ value: '', label: 'All statuses' }, ...statusOptions]}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          />
        </div>
      )}
    </div>
  );
}
